import axios, {
  AxiosError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../store/auth';
import { ensureCsrfToken, refreshCsrfToken, resetCsrfToken } from './csrf';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
const MUTATING_METHODS: HttpMethod[] = ['post', 'put', 'patch', 'delete'];

const configured =
  typeof import.meta.env.VITE_API_BASE_URL === 'string'
    ? import.meta.env.VITE_API_BASE_URL.trim()
    : '';
// En dev (proxy Vite) on attaque "/api" en relatif ; en prod on cible l'API.
const apiBaseUrl = configured.length > 0 ? configured : '/api';

export const http = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

function withHeader(config: InternalAxiosRequestConfig, key: string, value: string) {
  let headers: AxiosHeaders;
  if (config.headers instanceof AxiosHeaders) {
    headers = config.headers;
  } else {
    headers = new AxiosHeaders(config.headers ?? {});
    config.headers = headers;
  }
  headers.set(key, value);
}

let tokenRefreshPromise: Promise<void> | null = null;

http.interceptors.request.use(async (config) => {
  const method = (config.method ?? 'get').toLowerCase() as HttpMethod;
  if (MUTATING_METHODS.includes(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) withHeader(config, 'x-csrf-token', csrf);
  }
  const token = useAuthStore.getState().token;
  if (token) withHeader(config, 'Authorization', `Bearer ${token}`);
  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const message =
      typeof error.response?.data === 'object' && error.response?.data !== null
        ? (error.response.data as Record<string, unknown>).message
        : undefined;

    const requestUrl = String(error.config?.url ?? '');
    const isRefreshCall = requestUrl.includes('/v1/auth/refresh');
    const isLogoutCall = requestUrl.includes('/v1/auth/logout');

    // Refresh silencieux du JWT sur 401.
    if (
      status === 401 && !isRefreshCall && !isLogoutCall && error.config &&
      !(error.config as InternalAxiosRequestConfig & { _refreshRetry?: boolean })._refreshRetry
    ) {
      const { refreshToken } = useAuthStore.getState();
      if (refreshToken) {
        try {
          if (!tokenRefreshPromise) {
            tokenRefreshPromise = (async () => {
              const { data } = await http.post<{
                accessToken: string;
                refreshToken: string;
                expiresIn: number;
              }>(
                '/v1/auth/refresh',
                { refreshToken },
                { withCredentials: true },
              );
              useAuthStore.getState().setTokens(data);
            })().finally(() => {
              tokenRefreshPromise = null;
            });
          }
          await tokenRefreshPromise;
          const newToken = useAuthStore.getState().token;
          const config = error.config as InternalAxiosRequestConfig & {
            _refreshRetry?: boolean;
          };
          if (newToken && !config._refreshRetry) {
            config._refreshRetry = true;
            withHeader(config, 'Authorization', `Bearer ${newToken}`);
            return http.request(config);
          }
        } catch {
          useAuthStore.getState().clearAuth();
        }
      } else {
        useAuthStore.getState().clearAuth();
      }
    }

    // Rejeu sur CSRF invalide.
    if (
      status === 403 &&
      message === 'invalid csrf token' &&
      error.config &&
      !(error.config as InternalAxiosRequestConfig & { _csrfRetry?: boolean })
        ._csrfRetry
    ) {
      resetCsrfToken();
      const csrf = await refreshCsrfToken();
      if (csrf) {
        const config = error.config as InternalAxiosRequestConfig & {
          _csrfRetry?: boolean;
        };
        config._csrfRetry = true;
        withHeader(config, 'x-csrf-token', csrf);
        return http.request(config);
      }
    }

    if (typeof message === 'string' && message) {
      error.message = message;
    }
    return Promise.reject(error);
  },
);
